/**
 * The selling copy for a subscription tier, shared by the /subscription page
 * (PlanComponent) and the AI studio's pricing dialog (PricingDialogComponent), so
 * the two surfaces describe each plan in the same words.
 *
 * Only the prose lives here — every number on a card (credits, price, term)
 * comes from the credit pack, so copy cannot go stale against pricing.
 *
 * Keyed by the pack name lower-cased. A pack whose name is not listed (Essential
 * today) falls back to DEFAULT_COPY, so renaming or adding a pack in the admin
 * still renders.
 *
 * These describe the product, so they are not derived from the pack row and will
 * not follow it. If a tier stops selling video, this list still says it does.
 */
export interface TierCopy {
    /** One line under the plan name. */
    lead: string;
    /** The heading over the bullets — "Includes", or "Everything in Starter, and". */
    includesLead: string;
    includes: string[];
    /** The "Good to know" lines. */
    notes: string[];
}

export const TIER_COPY: { [key: string]: TierCopy } = {
    starter: {
        lead: 'For regular individual users trying out AI generation and editing.',
        includesLead: 'Includes',
        includes: [
            'All AI Models including imagen-4, gpt-image-1, gpt-image-2, nano-banana-pro, nano-banana',
            'Upscaling Models including Real-ESRGAN, Bria',
            'Video AI models including Seedance-1-Pro, HappyHorse-1.0, Wan-2.2-Fast, Wan-2.5',
            'All built-in editing tools included',
            'Text-to-video and image-to-video generation',
            'Access the full stock image library',
            'Standard Licence on every asset'
        ],
        notes: [
            'Yearly plans pool the year’s credits; monthly packs reset each month.',
            'Full-resolution downloads.'
        ]
    },
    pro: {
        lead: 'For agencies and studios with steady creative output.',
        includesLead: 'Everything in Starter, and',
        includes: [
            'Higher monthly throughput for busy pipelines',
            'All built-in editing tools (upscale, background removal, inpaint, erase)',
            'Full model suite — GPT Image, Nano Banana, Imagen, Nano Banana Pro',
            'Access the full stock image library',
            'Standard Licence on every asset'
        ],
        notes: [
            'Best value for agencies and brand teams.',
            'Need more? Ask about shared multi-user credit pools.'
        ]
    },
    business: {
        lead: 'For production teams with high-volume, high-mix creative needs.',
        includesLead: 'Everything in Pro, plus',
        includes: [
            'Largest monthly volume of the standard tiers',
            'Built for sustained, high-volume production',
            'Full model suite and video generation',
            'Access the full stock image library',
            'Standard Licence on every asset'
        ],
        notes: [
            'Best option for sustained, high-volume production.',
            'Invoice billing available.'
        ]
    }
};

export const DEFAULT_COPY: TierCopy = {
    lead: 'Credits you can spend across every AI tool and the stock library.',
    includesLead: 'Includes',
    includes: [
        'All built-in editing tools included',
        'Text-to-video and image-to-video generation',
        'Access the full stock image library',
        'Standard Licence on every asset'
    ],
    notes: [
        'Yearly plans pool the year’s credits; monthly packs reset each month.',
        'Full-resolution downloads.'
    ]
};

/** The copy for a pack name — its own when written, else DEFAULT_COPY. */
export function tierCopy(packName: string | null | undefined): TierCopy {
    return TIER_COPY[(packName || '').trim().toLowerCase()] || DEFAULT_COPY;
}
